import {
  CameraIcon,
  CameraIconS,
  HomeLogo,
  HomeLogoS,
  PaymentIcon,
  PaymentIconS,
  PayoutIcon,
  PayoutIconS,
  ProductIcon,
  ProductIconS,
  SettingIcon,
  SettingIconS,
} from "assets/images";
import RequestDot from "components/RequestDot/RequestDot";
import AuthContext from "context/AuthContext";
import DemoContext from "context/DemoContext";
import PaymentContext from "context/PaymentContext";
import ProfileContext from "context/ProfileContext";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./Sidebar.css";
import SidebarMenuIcon from "components/SidebarMenuIcon/SidebarMenuIcon";
import useCan from "hooks/useCan";
import { ROLES } from "utils/constants";
import { visibleStoreNavItems } from "./storeNavItems";

const ICON_SIZE = { width: "32px", height: "32px" };

/**
 * One store-portal menu entry, rendered from its STORE_NAV_ITEMS definition.
 */
const StoreNavItem = ({ item, active, badgeCount, onNavigate }) => {
  const showBadge = badgeCount > 0;

  return (
    <li
      onClick={() => onNavigate(item.path)}
      className={`nav-link ${active ? "active-item" : ""}`}
    >
      <span className="text nav-text">
        {item.remixIcon ? (
          <div className="sidebar-menu-item">
            <SidebarMenuIcon
              icon={(props) => React.createElement(item.remixIcon, props)}
              width={20}
              height={20}
              activeItem={active}
              fill={active ? "white" : "#8A8989"}
            />
            <span
              className={
                active ? "sidebar-menu-item-text-active" : "sidebar-menu-item-text"
              }
            >
              {item.label}
            </span>
          </div>
        ) : (
          <>
            <img
              src={active ? item.icon[1] : item.icon[0]}
              alt={item.label}
              style={{ ...ICON_SIZE, ...(item.iconStyle || {}) }}
            />
            {item.label} {showBadge && <RequestDot />}
          </>
        )}
      </span>
    </li>
  );
};

const Sideitems = ({ setIsActive }) => {
  let { user } = useContext(AuthContext);
  let { demoRequests } = useContext(DemoContext);
  let { updatedHosts } = useContext(ProfileContext);
  let { adminPaymentList } = useContext(PaymentContext);
  const { can, capabilities, isResolved } = useCan();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [location, setLocation] = useState("welcome");

  useEffect(() => {
    setLocation(pathname.split("/").slice(-1)[0]);
  }, [pathname]);

  const go = (path) => {
    navigate(path);
    setIsActive(false);
  };

  // Request-dot counters, keyed by the `badge` name in STORE_NAV_ITEMS.
  const badgeCounts = {
    updatedHosts: updatedHosts?.length || 0,
    demoRequests: demoRequests?.length || 0,
    adminPaymentList: adminPaymentList?.length || 0,
  };

  // Store-side roles are whoever holds at least one store capability. The
  // menu is the capability set, rendered - there is no per-role branch to fall
  // through, so a new role can never end up with an empty sidebar.
  if (capabilities.size > 0) {
    return (
      <>
        {visibleStoreNavItems(can).map((item, index) =>
          item.divider ? (
            <hr key={`divider-${index}`} />
          ) : (
            <StoreNavItem
              key={item.key}
              item={item}
              active={location === item.key}
              badgeCount={item.badge ? badgeCounts[item.badge] : 0}
              onNavigate={go}
            />
          )
        )}
      </>
    );
  }

  if (user?.role === ROLES.HOST.role) {
    return (
      <>
        <li
          onClick={() => go("/user/welcome")}
          className={`nav-link ${location === "welcome" ? "active-item" : ""}`}
        >
          <span className="text nav-text">
            <img
              src={location === "welcome" ? HomeLogoS : HomeLogo}
              alt="home"
              style={ICON_SIZE}
            />
            Dashboard
          </span>
        </li>
        <li
          onClick={() => go("/user/meetings")}
          className={`nav-link ${location === "meetings" ? "active-item" : ""}`}
        >
          <span className="text nav-text">
            <img
              src={location === "meetings" ? CameraIconS : CameraIcon}
              alt="home"
              style={ICON_SIZE}
            />
            My Video Meetings + Requests
            {demoRequests.length > 0 && <RequestDot />}
          </span>
        </li>
        <li
          onClick={() => go("/user/my-accounting")}
          className={`nav-link ${location === "my-accounting" ? "active-item" : ""}`}
        >
          <span className="text nav-text">
            <img
              src={location === "my-accounting" ? PayoutIconS : PaymentIcon}
              alt="home"
              style={ICON_SIZE}
            />
            My Accounting
          </span>
        </li>
        <li
          onClick={() => go("/user/my-meeting-history")}
          className={`nav-link ${
            location === "my-meeting-history" ? "active-item" : ""
          }`}
        >
          <span className="text nav-text">
            <img
              src={location === "my-meeting-history" ? CameraIconS : CameraIcon}
              alt="home"
              style={ICON_SIZE}
            />
            My Meeting History
          </span>
        </li>
        <li
          onClick={() => go("/user/showrooms")}
          className={`nav-link ${
            location === "showrooms" ? "active-item" : ""
          }`}
        >
          <span className="text nav-text">
            <img
              src={location === "showrooms" ? ProductIconS : ProductIcon}
              alt="home"
              style={ICON_SIZE}
            />
            Products
          </span>
        </li>
        <hr />
        <li
          onClick={() => go("/user/settings")}
          className={`nav-link ${location === "settings" ? "active-item" : ""}`}
        >
          <span className="text nav-text">
            <img
              src={location === "settings" ? SettingIconS : SettingIcon}
              alt="home"
              style={ICON_SIZE}
            />
            Settings
          </span>
        </li>
      </>
    );
  }

  if (user?.role === ROLES.PUREBRAND_ADMIN.role) {
    return (
      <>
        <li
          onClick={() => go("/user/welcome")}
          className={`nav-link ${location === "welcome" ? "active-item" : ""}`}
        >
          <span className="text nav-text">
            <img
              src={location === "welcome" ? HomeLogoS : HomeLogo}
              alt="home"
              style={ICON_SIZE}
            />
            Dashboard
          </span>
        </li>
        <li
          onClick={() => go("/user/store-hosts")}
          className={`nav-link ${
            location === "store-hosts" ? "active-item" : ""
          }`}
        >
          <span className="text nav-text">
            <img
              src={location === "store-hosts" ? PaymentIconS : PaymentIcon}
              alt="home"
              style={ICON_SIZE}
            />
            Real Customers
          </span>
        </li>
        <li
          onClick={() => go("/user/companies")}
          className={`nav-link ${
            location === "companies" ? "active-item" : ""
          }`}
        >
          <span className="text nav-text">
            <img
              src={location === "companies" ? PaymentIconS : PaymentIcon}
              alt="home"
              style={ICON_SIZE}
            />
            Companies
          </span>
        </li>
        <li
          onClick={() => go("/user/payments")}
          className={`nav-link ${location === "payments" ? "active-item" : ""}`}
        >
          <span className="text nav-text">
            <img
              src={location === "payments" ? PayoutIconS : PaymentIcon}
              alt="home"
              style={ICON_SIZE}
            />
            Accounting
          </span>
        </li>
        <li
          onClick={() => go("/user/recordings")}
          className={`nav-link ${
            location === "recordings" ? "active-item" : ""
          }`}
        >
          <span className="text nav-text">
            <img
              src={location === "recordings" ? CameraIconS : CameraIcon}
              alt="home"
              style={ICON_SIZE}
            />
            Recorded Meetings
          </span>
        </li>
        <li
          onClick={() => go("/user/review")}
          className={`nav-link ${location === "review" ? "active-item" : ""}`}
        >
          <span className="text nav-text">
            <img
              src={location === "review" ? ProductIconS : ProductIcon}
              alt="home"
              style={ICON_SIZE}
            />
            Review Approvals
          </span>
        </li>{" "}
        <hr />
        <li
          onClick={() => go("/user/settings")}
          className={`nav-link ${location === "settings" ? "active-item" : ""}`}
        >
          <span className="text nav-text">
            <img
              src={location === "settings" ? SettingIconS : SettingIcon}
              alt="home"
              style={ICON_SIZE}
            />
            Settings
          </span>
        </li>
      </>
    );
  }

  if (!isResolved) {
    // A store-side session whose capabilities are still being confirmed (for
    // example a login that predates this payload). Render nothing rather than
    // guess a menu the user may not have.
    return null;
  }

  // A logged-in user with no capabilities and no dedicated portal (an
  // unspecified role). Settings is the one page everyone has.
  return (
    <li
      onClick={() => go("/user/settings")}
      className={`nav-link ${location === "settings" ? "active-item" : ""}`}
    >
      <span className="text nav-text">
        <img
          src={location === "settings" ? SettingIconS : SettingIcon}
          alt="home"
          style={ICON_SIZE}
        />
        Settings
      </span>
    </li>
  );
};

export default Sideitems;
